import { useCallback, useContext, useMemo, useState } from 'react';

import { Clinician, OrganizationRole } from '@customTypes/clinician';
import { fetchOrganizationProviders, fetchClinicalTeamByProvider } from '@store/actions';
import { Context } from '@store/store';

interface IUseUsers {
  fetchUsers: (clinicId: string, fetchClinicalTeam?: boolean) => Promise<void>;
  clinicians: Clinician[];
  isLoading: boolean;
}

export default function useUsers(): IUseUsers {
  const [state, dispatch] = useContext(Context);
  const [isLoading, setIsLoading] = useState(false);

  const clinicians = useMemo(
    () => state.users.filter(user => (user.organizational_roles || []).includes(OrganizationRole.CLINICIAN)),
    [state.users],
  );

  const providerId = state.userProfile?.provider_id;
  const orderingProviderClinics = state.orderingProviderClinics;

  const fetchUsers = useCallback(
    async (clinicId: string, fetchClinicalTeam: boolean = false) => {
      if (clinicId && providerId) {
        setIsLoading(true);

        const isClinicOrderingProvider = orderingProviderClinics.some(clinic => clinic.clinic_id === clinicId);

        try {
          const promises = [
            fetchOrganizationProviders(clinicId).then(users => dispatch({ type: 'SET_USERS', payload: users })),
          ];

          if (fetchClinicalTeam && isClinicOrderingProvider) {
            promises.push(
              fetchClinicalTeamByProvider(clinicId, providerId).then(clinicalTeam =>
                dispatch({ type: 'SET_CLINICAL_TEAM', payload: clinicalTeam }),
              ),
            );
          }

          await Promise.all(promises);
        } catch (error) {
          console.error('Failed to fetch users:', error);
        } finally {
          setIsLoading(false);
        }
      }
    },
    [dispatch, providerId, orderingProviderClinics],
  );

  return {
    fetchUsers,
    clinicians,
    isLoading,
  };
}
